import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Button } from "@volca/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@volca/ui/components/ui/collapsible";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type {
  ReviewRule,
  TemplateSettings,
} from "@/profitability/settings/types";
import { formatReportPeriod } from "@/profitability/utils";
import { SettingsGroup, SettingsRow } from "./settings-group";

export const RuleHistory = ({
  rule,
  periods,
  runId,
  onSelect,
}: {
  rule: ReviewRule;
  periods: NonNullable<TemplateSettings["periods"]>;
  runId: string | null;
  onSelect: (id: string) => void;
}) => (
  <Collapsible>
    <CollapsibleTrigger
      render={
        <Button
          variant="ghost"
          className="h-auto w-full justify-between px-0 py-2 text-start"
        />
      }
    >
      <Stack render={<span />} gap={1} className="min-w-0">
        <Text render={<span />} variant="label">
          History and review outcomes
        </Text>
        <Text render={<span />} variant="caption" tone="muted">
          {rule.track.findings} records across {rule.track.of_periods} periods
        </Text>
      </Stack>
      <CaretDownIcon aria-hidden className="shrink-0 in-data-open:rotate-180" />
    </CollapsibleTrigger>
    <CollapsibleContent keepMounted>
      <SettingsGroup
        title="History"
        description="Review outcomes reflect current status."
      >
        <Stack gap={1} className="p-4">
          <Text tone="muted" variant="caption">
            {rule.track.not_an_issue} marked not an issue ·{" "}
            {rule.track.explained} explained · {rule.track.up} useful ratings ·{" "}
            {rule.track.down} noise ratings
          </Text>
        </Stack>
        {[...periods].reverse().map((period) => {
          const evaluation = rule.track.evaluation?.[period.run_id];
          const count = rule.track.counts[period.run_id];
          const label =
            evaluation === "disabled"
              ? "Off"
              : evaluation === "not_evaluated"
                ? "Not evaluated"
                : count !== undefined ||
                    evaluation === "evaluated" ||
                    evaluation === "partial"
                  ? `${count ?? 0} flagged${evaluation === "partial" ? " · Partial check" : ""}`
                  : "Not evaluated";
          return (
            <SettingsRow
              key={period.run_id}
              label={formatReportPeriod(period)}
              value={label}
              action={
                <Button
                  variant={runId === period.run_id ? "secondary" : "ghost"}
                  size="sm"
                  aria-pressed={runId === period.run_id}
                  aria-label={`${runId === period.run_id ? "Selected" : "View findings for"} ${formatReportPeriod(period)}`}
                  onClick={() => onSelect(period.run_id)}
                >
                  {runId === period.run_id ? "Selected" : "View findings"}
                </Button>
              }
            />
          );
        })}
        {periods.length === 0 && (
          <SettingsRow
            label="No periods yet"
            hint="History appears after this agent runs."
          />
        )}
      </SettingsGroup>
    </CollapsibleContent>
  </Collapsible>
);
